import { skipToken } from '@reduxjs/toolkit/query';
import { useMemo, useState } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';

import { Alert, Button, Typography } from '@/ui';

import {
  AssistantPageShell,
  AssistantSignOutButton,
} from './AssistantPageShell';
import {
  assistantErrorCode,
  assistantErrorStatus,
  useGetAssistantBookContextQuery,
  useGetAssistantCoachesQuery,
  useGetAssistedMembersQuery,
} from './api';
import {
  hasUnsettledAssistantBooking,
  useAssistantOperations,
} from './operationRecords';
import { assistantMemberPath } from './paths';
import { readAssistantSession, useAssistantSessionToken } from './session';
import { useAssistantBook } from './useAssistantBook';
import {
  ASSISTANT_HOME_PATH,
  useEndAssistantSessionOnUnauthorized,
  useSignOutAssistant,
} from './useAssistantSession';

import type {
  AssistantBookContext,
  AssistantBooking,
  AssistantCoach,
} from './api';
import type { AssistantBookProblem } from './useAssistantBook';
import type { FC, ReactNode } from 'react';

import { MentoCalDatePicker } from '@/components/MentoCal/DatePicker';
import {
  mentoCalBookingDayHeading,
  MentoCalTimeList,
} from '@/components/MentoCal/TimeList';
import { MentoCalTimeSlotButton } from '@/components/MentoCal/TimeSlotButton';
import { SeriesCoachConflictNote } from '@/features/appointments/SeriesCoachConflictNote';

const BOOKINGS: AssistantBooking[] = ['chemistry', 'series', 'single'];

const isBooking = (value: string | null): value is AssistantBooking =>
  BOOKINGS.includes(value as AssistantBooking);

const HEADINGS: Record<AssistantBooking, string> = {
  chemistry: 'Book a chemistry call',
  series: 'Book biweekly sessions',
  single: 'Book a single session',
};

/** Whether the coach's next step offers this booking at all. */
const offers = (coach: AssistantCoach, booking: AssistantBooking): boolean => {
  switch (booking) {
    case 'chemistry':
      return coach.nextStep === 'book_chemistry';
    case 'series':
      return coach.nextStep === 'book_series';
    case 'single':
    default:
      return coach.canBookSingle;
  }
};

/** A preview from the context; Moments sets the booked count itself. */
const assistantSeriesPhrase = (
  count: number | null,
  cadenceWeeks: number | null,
): string => {
  const sessions = count === null ? 'sessions' : `${count} sessions`;
  if (cadenceWeeks === 2) {
    return count === null ? 'biweekly sessions' : `${count} biweekly sessions`;
  }
  if (cadenceWeeks === 1) {
    return count === null ? 'weekly sessions' : `${count} weekly sessions`;
  }
  return cadenceWeeks === null
    ? sessions
    : `${sessions} every ${cadenceWeeks} weeks`;
};

const readViewerTimezone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

const CONTEXT_ERRORS: Record<string, string> = {
  coach_not_bookable:
    'Sessions with this coach can’t be booked online. Email concierge@mento.co and we’ll help.',
  member_engagement_end_missing:
    'This can’t be booked online right now. Email concierge@mento.co for help.',
  member_engagement_ended:
    'This person’s coaching has ended, so nothing can be booked online. Email concierge@mento.co for help.',
  not_bookable:
    'There’s nothing to book with this coach right now. Email concierge@mento.co if that looks wrong.',
};

const ineligibleMessage = (
  context: AssistantBookContext,
  coachName: string,
  memberName: string,
): string => {
  if (context.ineligibleReasons.includes('active_series_exists')) {
    return `${memberName} already has sessions booked with ${coachName}.`;
  }
  if (context.ineligibleReasons.includes('coach_unavailable')) {
    return `${coachName} has no times open for booking right now. Email concierge@mento.co and we’ll help.`;
  }
  return 'There are no times to book online. Email concierge@mento.co and we’ll help.';
};

const BookProblemAlert: FC<{ problem: AssistantBookProblem | null }> = ({
  problem,
}) => {
  if (problem === 'in_progress') {
    return (
      <Alert role="alert" severity="warning">
        A booking with this coach is still in progress. Go back to see how it
        went before booking again.
      </Alert>
    );
  }
  if (problem === 'storage_unavailable') {
    return (
      <Alert role="alert" severity="error">
        This browser isn&apos;t letting Mento save the request, so nothing was
        sent. Allow site data for Mento, or use another browser.
      </Alert>
    );
  }
  return null;
};

const BookPicker: FC<{
  booking: AssistantBooking;
  coach: AssistantCoach;
  context: AssistantBookContext;
  memberId: string;
  memberName: string;
  viewerTimezone: string;
}> = ({ booking, coach, context, memberId, memberName, viewerTimezone }) => {
  const { problem, submit } = useAssistantBook({
    booking,
    coach,
    memberId,
    viewerTimezone,
  });
  const firstOpenDay = context.days.find(
    (day) => day.enabled && day.slots.length > 0,
  );
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedStart, setSelectedStart] = useState<string | null>(null);
  const shownDate = selectedDate || firstOpenDay?.date || '';
  const slots = context.days.find((day) => day.date === shownDate)?.slots ?? [];
  const selectedSlot = slots.find((slot) => slot.startAt === selectedStart);
  const series = booking === 'series';

  return (
    <div className="flex flex-col gap-4">
      <Typography variant="h5">
        Choose a time for {memberName} with {coach.firstName}
      </Typography>
      <Typography color="text.secondary" variant="body2">
        {series
          ? `${assistantSeriesPhrase(context.appointmentCount, context.cadenceWeeks)}, ${context.durationMinutes} minutes each, starting on the time you choose. `
          : `${context.durationMinutes} minutes. `}
        Times are in {context.timezone}.
      </Typography>
      <div className="flex flex-col gap-6 md:flex-row">
        <MentoCalDatePicker
          days={context.days}
          selectedDate={shownDate}
          onSelectDate={(date) => {
            setSelectedDate(date);
            setSelectedStart(null);
          }}
        />
        <MentoCalTimeList
          className="flex-1"
          heading={mentoCalBookingDayHeading(shownDate)}
          scrollClassName="max-h-80"
        >
          {slots.map((slot) => (
            <MentoCalTimeSlotButton
              key={slot.startAt}
              label={slot.label}
              selected={slot.startAt === selectedStart}
              onClick={() => setSelectedStart(slot.startAt)}
            />
          ))}
        </MentoCalTimeList>
      </div>
      {series && selectedSlot ? (
        <SeriesCoachConflictNote
          conflictingOccurrenceStarts={selectedSlot.coachConflictStarts}
          scope="series"
          timeZone={context.timezone}
        />
      ) : null}
      <BookProblemAlert problem={problem} />
      <div>
        <Button
          disabled={!selectedSlot}
          variant="contained"
          onClick={() => {
            if (selectedSlot) submit(selectedSlot);
          }}
        >
          {selectedSlot
            ? `Book ${mentoCalBookingDayHeading(shownDate)}, ${selectedSlot.label}`
            : 'Choose a time'}
        </Button>
      </div>
    </div>
  );
};

const BookForm: FC<{
  booking: AssistantBooking;
  coach: AssistantCoach;
  memberId: string;
  memberName: string;
}> = ({ booking, coach, memberId, memberName }) => {
  const timezone = useMemo(readViewerTimezone, []);
  const context = useGetAssistantBookContextQuery(
    {
      booking,
      coachPersonId: coach.personId,
      memberId,
      viewerTimezone: timezone,
    },
    { refetchOnMountOrArgChange: true },
  );
  useEndAssistantSessionOnUnauthorized(context.error);

  if (context.isError) {
    const message = CONTEXT_ERRORS[assistantErrorCode(context.error) ?? ''];
    return (
      <Alert severity={message ? 'info' : 'error'}>
        {message ??
          'We couldn’t load available times. Go back and try again in a moment.'}
      </Alert>
    );
  }
  if (!context.currentData) {
    return <Typography variant="body1">Loading available times…</Typography>;
  }
  const hasOpenDay = context.currentData.days.some(
    (day) => day.enabled && day.slots.length > 0,
  );
  if (!context.currentData.eligible || !hasOpenDay) {
    return (
      <Alert severity="info">
        {ineligibleMessage(context.currentData, coach.firstName, memberName)}
      </Alert>
    );
  }
  return (
    <BookPicker
      booking={booking}
      coach={coach}
      context={context.currentData}
      memberId={memberId}
      memberName={memberName}
      viewerTimezone={timezone}
    />
  );
};

const BookBody: FC<{
  booking: AssistantBooking;
  coachPersonId: string;
  memberId: string;
}> = ({ booking, coachPersonId, memberId }) => {
  const token = useAssistantSessionToken();
  const members = useGetAssistedMembersQuery(token ? undefined : skipToken);
  // Always the current next step: it decides whether this booking is offered.
  const coaches = useGetAssistantCoachesQuery(token ? memberId : skipToken, {
    refetchOnMountOrArgChange: true,
  });
  const { refetch: refetchCoaches } = coaches;
  const records = useAssistantOperations(memberId);
  const { isSigningOut, signOut } = useSignOutAssistant();
  useEndAssistantSessionOnUnauthorized(members.error, coaches.error);

  const memberName =
    members.currentData?.find((member) => member.memberId === memberId)
      ?.displayName ?? 'this person';
  const coach = coaches.currentData?.find(
    (candidate) => candidate.personId === coachPersonId,
  );
  const backLink = (
    <Link className="underline" to={assistantMemberPath(memberId)}>
      Back to {memberName}&apos;s sessions
    </Link>
  );

  let body: ReactNode;
  if (assistantErrorStatus(coaches.error) === 403) {
    body = (
      <div className="flex flex-col gap-3" role="alert">
        <Typography variant="body1">
          You&apos;re not a scheduling assistant for this person anymore.
        </Typography>
        <div>
          <Link className="underline" to={ASSISTANT_HOME_PATH}>
            Back to the people you assist
          </Link>
        </div>
      </div>
    );
  } else if (coaches.isError) {
    body = (
      <div className="flex flex-col gap-3">
        <Typography variant="body1">
          We couldn&apos;t load this coach.
        </Typography>
        <div>
          <Button variant="outlined" onClick={refetchCoaches}>
            Retry
          </Button>
        </div>
      </div>
    );
  } else if (!coaches.currentData) {
    body = <Typography variant="body1">Loading…</Typography>;
  } else if (!coach || coach.nextStep === 'not_bookable') {
    body = (
      <div className="flex flex-col gap-3" role="alert">
        <Typography variant="body1">
          This coach can&apos;t be booked online for {memberName}. Email
          concierge@mento.co and we&apos;ll help.
        </Typography>
        <div>{backLink}</div>
      </div>
    );
  } else if (hasUnsettledAssistantBooking(records, coach.personId)) {
    // No second booking while one has no answer: finish that one first.
    body = (
      <div className="flex flex-col gap-3" role="status">
        <Typography variant="body1">
          A booking with {coach.firstName} is still in progress.
        </Typography>
        <div>{backLink}</div>
      </div>
    );
  } else if (!offers(coach, booking)) {
    body = (
      <div className="flex flex-col gap-3" role="status">
        <Typography variant="body1">
          There&apos;s nothing like this to book with {coach.firstName} right
          now. Check {memberName}&apos;s sessions for what is already booked.
        </Typography>
        <div>{backLink}</div>
      </div>
    );
  } else {
    body = (
      <div className="flex flex-col gap-4">
        <Typography variant="h4">{HEADINGS[booking]}</Typography>
        <BookForm
          booking={booking}
          coach={coach}
          memberId={memberId}
          memberName={memberName}
        />
        <div>{backLink}</div>
      </div>
    );
  }

  return (
    <AssistantPageShell
      headerActions={
        <AssistantSignOutButton disabled={isSigningOut} onSignOut={signOut} />
      }
    >
      {body}
    </AssistantPageShell>
  );
};

/** `/assistant/members/:memberId/book/:coachPersonId?booking=chemistry|series|single` */
const AssistantBook: FC = () => {
  const { coachPersonId, memberId } = useParams();
  const [searchParams] = useSearchParams();
  const booking = searchParams.get('booking');
  // Checked on arrival only, as on the member page.
  const arrivedWithSession = useMemo(() => readAssistantSession() !== null, []);

  if (!memberId || !arrivedWithSession) {
    return <Navigate to={ASSISTANT_HOME_PATH} replace />;
  }
  if (!coachPersonId || !isBooking(booking)) {
    return <Navigate to={assistantMemberPath(memberId)} replace />;
  }
  return (
    <BookBody
      key={`${memberId}:${coachPersonId}:${booking}`}
      booking={booking}
      coachPersonId={coachPersonId}
      memberId={memberId}
    />
  );
};

export default AssistantBook;
